import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { ASK_HELP_QUESTION } from '../../graphql/helpContent';
import { getHelpArticlePath } from './helpConstants';

export default function HelpAskPanel() {
  const [question, setQuestion] = useState('');
  const [askHelpQuestion, { data, loading, error, called }] = useLazyQuery(ASK_HELP_QUESTION, {
    fetchPolicy: 'network-only',
  });
  const answer = data?.askHelpQuestion;

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = question.trim();
    if (!trimmed || loading) return;
    askHelpQuestion({ variables: { question: trimmed } });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      handleSubmit(event);
    }
  };

  return (
    <section className="rounded-xl border border-sky-100 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900">Ask how to do something</h2>
      <p className="mt-1 text-sm text-gray-600">
        Questions are answered from the help files for your role, not from the database.
      </p>
      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label htmlFor="help-question" className="sr-only">Ask a help question</label>
        <input
          id="help-question"
          type="text"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Example: How do I assign a job?"
          className="h-11 w-full rounded-lg border border-gray-300 bg-gray-50 px-3 text-gray-900 placeholder-gray-500 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20"
        />
        <button
          type="submit"
          disabled={loading || !question.trim()}
          className="inline-flex h-11 items-center justify-center rounded-lg bg-sky-700 px-4 text-sm font-medium text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Searching…' : 'Ask'}
        </button>
      </form>
      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">Could not search help right now. Try again.</p>
      )}
      {called && !loading && answer && (
        <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
          <p className="whitespace-pre-wrap text-sm text-gray-800">{answer.answer}</p>
          {answer.usedAi && (
            <p className="mt-2 text-xs text-gray-500">Answered from help files with AI.</p>
          )}
          {answer.sources?.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Related articles</p>
              {answer.sources.map((source) => (
                <Link
                  key={source.slug}
                  to={getHelpArticlePath(source)}
                  className="block rounded-md bg-white px-3 py-2 text-sm font-medium text-sky-800 ring-1 ring-gray-200 hover:bg-sky-50"
                >
                  {source.title}
                  {source.summary ? <span className="block font-normal text-gray-500">{source.summary}</span> : null}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
